import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { AppDispatch } from "../../../redux/store";
import ToastComponent from "../../../components/toast";

import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import {
  clearUpdateUserState,
  getToggleUserStatusState,
  getUpdateUserState,
  UpdateUserRequest,
  updateUserStateAction,
  UserWorkingTime,
} from "../../../redux/features/user-slice";
import { Formik } from "formik";
import Input from "../../../components/input";
import { isValidTime } from "../../../core/utils";
import Button from "../../../components/button";
import { MinusOutlined } from "../../../components/icons/minus-outlined";
import { AddOutlined } from "../../../components/icons/add-outlined";
import {
  getUserState,
  getUserStateAction,
} from "../../../redux/features/auth-slice";

export default function ProfilePage() {
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(getUserState).data;
  const updateUserState = useSelector(getUpdateUserState);
  const toggleUserStatusState = useSelector(getToggleUserStatusState);
  const errorState = useSelector(getErrorState);

  const [newWorkingTime, setNewWorkingTime] = useState<UserWorkingTime>({
    startTime: "00:00",
    endTime: "00:00",
  });

  const submitUpdateUser = async (values: UpdateUserRequest) => {
    await dispatch(updateUserStateAction(values));
  };

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  useEffect(() => {
    if (user) {
      dispatch(
        setBreadcrumbState([
          { id: "settings", path: null, title: "Settings" },
          {
            id: "profile",
            path: null,
            title: "Profile",
          },
        ])
      );
    }
  }, [user]);

  useEffect(() => {
    if (updateUserState.data && updateUserState.data.success) {
      handleSuccessToast(updateUserState.data.message, clearUpdateUserState);
      const fetchUserData = async () => {
        await dispatch(getUserStateAction());
      };
      fetchUserData();
    }
  }, [updateUserState, handleSuccessToast]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState]);

  return (
    <>
      {user && (
        <>
          <Helmet title={user.name} />
          <Formik
            initialValues={{
              name: user.name,
              email: user.email,
              positionId: user.position.id,
              experienceId: user.experience.id,
              customerId: user.customer?.id ?? null,
              categoryId: user.category?.id ?? null,
              role: user.role,
              misaCode: user.misaCode,
              teamId: user.team.id,
              groupTeamId: user.groupTeam?.id ?? null,
              workingTimes: user.workingTimes,
            }}
            validationSchema={Yup.object().shape({
              name: Yup.string().required("Name is required"),
              email: Yup.string()
                .email("Must be a valid email")
                .max(50)
                .required("Email is required"),
              positionId: Yup.string().required("Position is required"),
              teamId: Yup.string().required("Team is required"),
              workingTimes: Yup.array().min(1, "Working Times is required"),
            })}
            onSubmit={(values, { setSubmitting }) => {
              setSubmitting(false);
              submitUpdateUser({
                ...values,
                id: user.id,
                misaCode: undefined,
              } as UpdateUserRequest);
            }}
          >
            {({
              values,
              errors,
              handleChange,
              handleSubmit,
              isSubmitting,
              setValues,
              dirty,
            }) => (
              <form
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
                className="h-full overflow-y-auto"
              >
                <div
                  className="h-full grid grid-cols-2 gap-8 overflow-y-scroll"
                  style={{
                    height: "calc(100% - 40px)",
                  }}
                >
                  <div className="space-y-4">
                    <Input
                      label="Name *"
                      type="text"
                      id="name"
                      value={values.name}
                      onChange={handleChange}
                      errorText={errors.name}
                    />
                    <Input
                      label="Email *"
                      type="email"
                      id="email"
                      value={values.email}
                      onChange={handleChange}
                      errorText={errors.email}
                    />
                    <Input
                      label="Team"
                      type="text"
                      id="groupTeam"
                      value={user.groupTeam?.name ?? "--"}
                      readonly
                    />
                    <Input
                      label="Location *"
                      type="text"
                      id="team"
                      value={user.team.name}
                      readonly
                    />
                    <Input
                      label="Position *"
                      type="text"
                      id="position"
                      value={user.position.name}
                      readonly
                    />
                    <Input
                      label="Experience Level *"
                      type="text"
                      id="experience"
                      value={user.experience.title}
                      readonly
                    />
                    <Input
                      label="Customer"
                      type="text"
                      id="customer"
                      value={user.customer?.name ?? "--/--"}
                      readonly
                    />
                    <Input
                      label="Employment Category *"
                      type="text"
                      id="category"
                      value={user.category?.title ?? "--/--"}
                      readonly
                    />
                    <Input
                      label="Role *"
                      type="text"
                      id="role"
                      value={values.role.capitalize()}
                      readonly
                    />
                  </div>
                  <div className="space-y-4">
                    <div className="flex flex-col bg-background-default p-4 rounded-lg shadow">
                      <label className="text-xs text-text-title">
                        Working Times
                      </label>
                      {values.workingTimes.map((time: UserWorkingTime) => (
                        <div className="flex flex-col">
                          <div className="flex items-center space-x-2">
                            <div className="w-1/2 flex items-center justify-between border border-divider p-2 rounded-lg">
                              <label className="text-sm">
                                {time.startTime}
                              </label>
                              <hr className="flex-1 border border-dashed border-divider mx-2" />
                              <label className="text-sm">{time.endTime}</label>
                            </div>
                            <div
                              className="rounded-lg hover:bg-action-hover p-1 cursor-pointer"
                              onClick={() => {
                                if (values.workingTimes.length > 1) {
                                  setValues({
                                    ...values,
                                    workingTimes: values.workingTimes.filter(
                                      (e) =>
                                        e.startTime !== time.startTime &&
                                        e.endTime !== time.endTime
                                    ),
                                  });
                                }
                              }}
                            >
                              <MinusOutlined
                                className={`h-5 w-5 ${
                                  values.workingTimes.length > 1
                                    ? "text-error-main"
                                    : "text-action-disabled"
                                }`}
                              />
                            </div>
                          </div>
                          <label className="text-xs text-text-title italic py-2">
                            or
                          </label>
                        </div>
                      ))}
                      <div className="flex items-center space-x-2">
                        <div className="w-1/2 flex items-center space-x-2">
                          <Input
                            type="text"
                            value={newWorkingTime.startTime}
                            onChange={(event) =>
                              setNewWorkingTime({
                                ...newWorkingTime,
                                startTime: event.target.value,
                              })
                            }
                            exClassName="text-center"
                          />
                          <label className="text-xs text-text-title">to</label>
                          <Input
                            type="tetx"
                            value={newWorkingTime.endTime}
                            onChange={(event) =>
                              setNewWorkingTime({
                                ...newWorkingTime,
                                endTime: event.target.value,
                              })
                            }
                            exClassName="text-center"
                          />
                        </div>
                        <div
                          className="rounded-lg hover:bg-action-hover p-1 cursor-pointer"
                          onClick={() => {
                            if (
                              isValidTime(newWorkingTime.startTime) &&
                              isValidTime(newWorkingTime.endTime)
                            ) {
                              setValues({
                                ...values,
                                workingTimes: [
                                  ...values.workingTimes,
                                  newWorkingTime,
                                ],
                              });
                            }
                          }}
                        >
                          <AddOutlined
                            className={`w-5 h-5 ${
                              isValidTime(newWorkingTime.startTime) &&
                              isValidTime(newWorkingTime.endTime)
                                ? "text-success-main"
                                : "text-action-disabled"
                            } `}
                          />
                        </div>
                      </div>
                      {errors.workingTimes && (
                        <p className="text-error-main text-xs">
                          {errors.workingTimes as string}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <Button
                    label="Save Changes"
                    type="submit"
                    loading={
                      updateUserState.loading || toggleUserStatusState.loading
                    }
                  />
                </div>
              </form>
            )}
          </Formik>
        </>
      )}
      <ToastComponent id="profile" />
    </>
  );
}
